import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';

import { getClassAuditRows, getClassCanonicalLevels, listClassRules } from '../services/classAuditApi';
import {
  MAPPING_SOURCE, hasInstructorNotTracked, needsAttention, isFullyConfigured,
} from '../utils/classQualityChecks';
import { classNameStem } from '../utils/classNameStem';
import { downloadClassAuditCsv, buildFilename } from '../utils/classAuditCsv';
import ClassAuditList from './ClassAuditList';
import MissingLevelsInsights from './MissingLevelsInsights';

// Class Audit -- level-only MVP (Phase 1). Slice 6b: controls + fetch (no grid rows or
// assignment controls yet -- those are 6c / 6d). The frontend chooses the tracking year
// (the API infers nothing) and opens on the actionable Missing Level view.

// Tracking-year options: MIN..current+1, newest first. The default is the current year.
const MIN_TRACKING_YEAR = 2016;
const CURRENT_YEAR = new Date().getFullYear();
const TRACKING_YEARS = [];
for (let y = CURRENT_YEAR + 1; y >= MIN_TRACKING_YEAR; y--) TRACKING_YEARS.push(y);

// Quick views. Two kinds:
//   * mappingSource views -> server-filtered on r.mapping_source (works with the row cap).
//   * predicate views     -> the server fetch omits mapping_source (all states in scope) and the
//     client filters with a shared classQualityChecks predicate. Instructor-tracking and the
//     combined Needs Attention / Fully Configured states aren't a single mapping_source, so they
//     can't be server-filtered without an API change; client-side filtering keeps this a UI slice.
//     For a franchise-scoped view the fetch is well under the cap, so the filtered set is complete.
//     LIMITATION (MVP, accepted): when the underlying request is TRUNCATED at the 5,000-row cap
//     (e.g. a fleet-wide, single-year view), a client-filtered quality view (Missing Instructors /
//     Needs Attention / Fully Configured) is INCOMPLETE and represents only the returned subset.
//     The existing truncation banner stays visible in these views to disclose it; server-side
//     quality filters are intentionally NOT added in this slice.
// Default stays Missing levels so instructor-tracking gaps don't crowd out level stewardship.
// A THIRD kind joins the two above: `ambiguous` views are ALSO server-filtered (params.ambiguous),
// deliberately NOT a client predicate. Unresolved ambiguity is rare and scattered across history --
// the only live instance sits in TY2021 among 24,226 eligible classes, ~5x the row cap -- so a
// client-side filter over a truncated page could miss the very rows the view exists to surface.
const QUICK_VIEWS = [
  { key: 'missing_levels',       label: 'Missing levels',       mappingSource: MAPPING_SOURCE.UNASSIGNED },
  { key: 'ambiguous',            label: 'Ambiguous mapping',    ambiguous: true },
  { key: 'missing_instructors',  label: 'Missing instructors',  predicate: hasInstructorNotTracked },
  { key: 'needs_attention',      label: 'Needs attention',      predicate: needsAttention },
  { key: 'fully_configured',     label: 'Fully configured',     predicate: isFullyConfigured },
  { key: 'automatically_mapped', label: 'Automatically mapped', mappingSource: MAPPING_SOURCE.AUTO_SUGGESTION },
  { key: 'manual_assignments',   label: 'Manual assignments',   mappingSource: MAPPING_SOURCE.MANUAL_ASSIGNMENT },
  { key: 'manual_overrides',     label: 'Manual overrides',     mappingSource: MAPPING_SOURCE.MANUAL_OVERRIDE },
  { key: 'all',                  label: 'All classes' },
];
const DEFAULT_VIEW_KEY = 'missing_levels';

const TRACKING_SEASONS = [
  { value: '', label: 'All seasons' },
  { value: 1, label: 'Spring' },
  { value: 2, label: 'Summer' },
  { value: 3, label: 'Fall' },
  { value: 4, label: 'Winter' },
];

const SEARCH_DEBOUNCE_MS = 400;

export default function ClassAudit(props) {
  const franchises = props.comcodes || [];

  // ── filter state ──
  const [trackingYear, setTrackingYear] = useState(CURRENT_YEAR);
  const [quickViewKey, setQuickViewKey] = useState(DEFAULT_VIEW_KEY); // default: Missing levels
  const [comCode, setComCode] = useState('');            // '' = all franchises
  const [facilityName, setFacilityName] = useState('');  // '' = all facilities
  const [trackingSeason, setTrackingSeason] = useState(''); // '' = all seasons
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Candidate selection from the Missing Levels panel. This is a CLIENT-SIDE grouping filter
  // (classNameStem equality) over the already-loaded rows -- the SAME relation the candidate counts
  // use -- NOT the server substring search. The grouping stem is a normalized key, not guaranteed to
  // be a literal LIKE-substring of the class names (orphan punctuation, collapsed internal
  // whitespace, mid-name schedule tokens), so routing it through the search box returned zero rows.
  // Filtering the loaded rows by stem equality guarantees a click shows exactly the counted group.
  const [selectedStem, setSelectedStem] = useState(null);

  // Canonical-level catalog: loaded ONCE at page level; the panel filters it by the class's
  // program type. Loaded independently of the row fetch.
  const [levels, setLevels] = useState([]);

  // ── result state ──
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [maxRows, setMaxRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Facility options are derived from the "all facilities" result and frozen while a facility
  // is selected, so picking one doesn't collapse the dropdown to just itself.
  const [facilityOptions, setFacilityOptions] = useState([]);

  // Request sequencing: only the newest response may apply -> a slow older response can never
  // overwrite a newer filter result.
  const requestSeq = useRef(0);

  // ── S5c: page-level pending-rules state ──
  // AUTHORITATIVE current pending state, DERIVED FRESH on every fetch from listClassRules -- the
  // same endpoint and the same canonical `pending_rebuild` (RULE_PENDING_SELECT) Rule Management
  // renders. No second definition, no new endpoint, and nothing persisted client-side: that is what
  // makes the banner self-correcting when a dbt build lands.
  const [pendingRuleCount, setPendingRuleCount] = useState(0);
  const [warehouseBuiltAt, setWarehouseBuiltAt] = useState(null);
  const rulesSeq = useRef(0);

  // Load the canonical-level catalog once on mount.
  useEffect(() => {
    let cancelled = false;
    getClassCanonicalLevels()
      .then((res) => { if (!cancelled) setLevels(res.levels || []); })
      .catch(() => { if (!cancelled) setLevels([]); });
    return () => { cancelled = true; };
  }, []);

  // Merge a resolved write preview into the matching row in place, so an assign/remove updates
  // the grid immediately (no refetch). The row stays visible with its new status; a resolved
  // row leaves an issue view only on the NEXT fetch (server-filtered), not mid-action.
  const handleRowUpdate = (preview) => {
    if (!preview || !preview.team_dim_id) return;
    setRows((prev) => prev.map((r) => (r.team_dim_id === preview.team_dim_id ? { ...r, ...preview } : r)));
  };

  // Debounce the class-name search: typing updates searchInput immediately, but the fetch keys
  // off debouncedSearch, so we don't request on every keystroke.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [searchInput]);

  // A candidate pattern selection is scoped to the currently loaded population, so any change to the
  // scope filters (year / franchise / facility / season / quick view) invalidates it. Typing in
  // Search and the chip's clear control reset it inline (below). This effect covers the rest in one
  // place; it never runs on a candidate click (none of these deps change then).
  useEffect(() => {
    setSelectedStem(null);
  }, [trackingYear, comCode, facilityName, trackingSeason, quickViewKey]);

  // The active quick view. mappingSource views are server-filtered; predicate views fetch all
  // states in scope and filter client-side (below).
  const activeView = QUICK_VIEWS.find((v) => v.key === quickViewKey) || QUICK_VIEWS[0];
  const activeMappingSource = activeView.mappingSource; // string, or undefined for predicate/all views
  const activeAmbiguous = activeView.ambiguous ? '1' : undefined; // server-filtered, like mappingSource

  // Fetch whenever an APPLIED filter changes. Switching between two predicate views does NOT refetch
  // (both omit mapping_source) -- only the client filter below changes.
  useEffect(() => {
    // Guard: no request until a valid tracking year is available.
    if (!Number.isInteger(trackingYear)) return;

    const seq = ++requestSeq.current;
    setLoading(true);
    setError('');

    const params = { trackingyear: trackingYear };
    if (activeMappingSource) params.mapping_source = activeMappingSource; // predicate/all views omit it
    if (activeAmbiguous) params.ambiguous = activeAmbiguous;
    if (comCode) params.com_code = comCode;
    if (facilityName) params.facility_name = facilityName;
    if (trackingSeason !== '') params.trackingseason = trackingSeason;
    if (debouncedSearch) params.search = debouncedSearch;

    getClassAuditRows(params)
      .then((res) => {
        if (seq !== requestSeq.current) return; // a newer request superseded this one
        const nextRows = res.rows || [];
        setRows(nextRows);
        setTruncated(Boolean(res.truncated));
        setMaxRows(res.max_rows ?? null);
        // Refresh the facility list only from an all-facilities result (freeze when filtered).
        // KNOWN LIMITATION: when the *All classes* view is truncated at the row cap, this derived
        // facility list may be incomplete (facilities only appear if a class of theirs is within
        // the returned subset). Acceptable for MVP -- the primary Missing Level view stays below
        // the annual cap -- and we intentionally do NOT add a facility endpoint to solve it now.
        if (facilityName === '') {
          const names = Array.from(new Set(nextRows.map((r) => r.facility_name).filter(Boolean))).sort();
          setFacilityOptions(names);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setError(err.message || 'Failed to load classes.');
        setRows([]);
        setTruncated(false);
        setLoading(false);
      });
  }, [trackingYear, activeMappingSource, activeAmbiguous, comCode, facilityName, trackingSeason, debouncedSearch]);

  // Pending-rules banner state, re-derived on EXACTLY the same events as the grid fetch above, so
  // "the banner is as current as the rows" is a single invariant rather than two cadences. The rule
  // list is not filter-scoped (a rule is global to the warehouse build, not to this view), so the
  // repeat calls are the cost of that invariant; the query is a ~160-row read.
  //
  // TRI-STATE, matching Rule Management: `pending_rebuild` is NULL when the suggestions model has no
  // build timestamp, so `=== true` is load-bearing -- an unknown must never be counted as pending.
  // A failed call resets to zero for the same reason: absence of evidence is not evidence of pending.
  useEffect(() => {
    if (!Number.isInteger(trackingYear)) return;
    const seq = ++rulesSeq.current;
    listClassRules({ is_active: '1' })
      .then((res) => {
        if (seq !== rulesSeq.current) return;
        const rules = res.rules || [];
        setPendingRuleCount(rules.filter((r) => r.pending_rebuild === true).length);
        setWarehouseBuiltAt((res.warehouse && res.warehouse.suggestions_built_at) || null);
      })
      .catch(() => {
        if (seq !== rulesSeq.current) return;
        setPendingRuleCount(0);
        setWarehouseBuiltAt(null);
      });
  }, [trackingYear, activeMappingSource, activeAmbiguous, comCode, facilityName, trackingSeason, debouncedSearch]);

  // Client-side filter for predicate quick views (instructor / combined states). mappingSource
  // views already arrived server-filtered, so their predicate is undefined -> rows pass through.
  // This is the population the candidate panel groups and counts over.
  const predicateRows = useMemo(
    () => (activeView.predicate ? rows.filter(activeView.predicate) : rows),
    [rows, activeView],
  );

  // The grid rows. When a candidate pattern is selected, narrow to its stem group using the SAME
  // population (predicateRows) and the SAME equality relation (classNameStem) the candidate counts
  // use -- so a candidate showing x7 renders exactly those 7 rows, regardless of whether the stem is
  // a literal substring of the class names. The candidate panel keeps grouping over predicateRows,
  // so every candidate stays visible while one is selected.
  const displayedRows = useMemo(
    () => (selectedStem
      ? predicateRows.filter((r) => classNameStem(r.class_name) === selectedStem)
      : predicateRows),
    [predicateRows, selectedStem],
  );

  // Changing the franchise resets the facility in the same batch (facilities are franchise-
  // scoped) -> a single fetch, not two.
  const handleFranchiseChange = (event) => {
    setComCode(event.target.value);
    setFacilityName('');
  };

  // CSV export of the CURRENT view. Serializes the page's own displayedRows (no second API call).
  // Disabled when a load/error is in flight, when there are no rows, or when the underlying result
  // is truncated -- we never export a partial file that looks complete (§ truncation).
  const canExportCsv = !loading && !error && !truncated && displayedRows.length > 0;
  const handleDownloadCsv = () => {
    if (!canExportCsv) return;
    let franchiseLabel = '';
    if (comCode) {
      const f = franchises.find((x) => (x.comcode ?? x.comCode ?? x.com_code) === comCode);
      franchiseLabel = (f && (f.franchise_name ?? f.franchiseName)) || comCode;
    }
    const filename = buildFilename({
      trackingYear,
      franchiseLabel,
      quickViewLabel: activeView.label,
    });
    downloadClassAuditCsv(displayedRows, filename);
  };

  // ── extract .map()s out of JSX (house rule) ──
  const trackingYearItems = TRACKING_YEARS.map((y) => (
    <MenuItem key={y} value={y}>{y}</MenuItem>
  ));
  const franchiseItems = franchises.map((f) => {
    const code = f.comcode ?? f.comCode ?? f.com_code;
    const name = f.franchise_name ?? f.franchiseName ?? code;
    return <MenuItem key={code} value={code}>{name}</MenuItem>;
  });
  const facilityItems = facilityOptions.map((name) => (
    <MenuItem key={name} value={name}>{name}</MenuItem>
  ));
  const trackingSeasonItems = TRACKING_SEASONS.map((s) => (
    <MenuItem key={String(s.value)} value={s.value}>{s.label}</MenuItem>
  ));
  const quickViewButtons = QUICK_VIEWS.map((v) => (
    <Button
      key={v.key}
      size="small"
      variant={quickViewKey === v.key ? 'contained' : 'outlined'}
      onClick={() => setQuickViewKey(v.key)}
      sx={{ mr: 1, mb: 1 }}
    >
      {v.label}
    </Button>
  ));

  // ── body: loading / error / empty / grid (Slice 6c) ──
  let body;
  if (loading) {
    body = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">Loading classes…</Typography>
      </Box>
    );
  } else if (error) {
    body = <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  } else if (displayedRows.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No classes match these filters.
      </Typography>
    );
  } else {
    body = (
      <>
        {/* Read-only triage aid, Missing Levels only: names recurring in the loaded view. */}
        {quickViewKey === 'missing_levels' && (
          <MissingLevelsInsights
            rows={predicateRows}
            truncated={truncated}
            onSelectPattern={setSelectedStem}
            levels={levels}
            trackingyear={trackingYear}
          />
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {displayedRows.length} class{displayedRows.length === 1 ? '' : 'es'}
        </Typography>
        <ClassAuditList rows={displayedRows} levels={levels} onRowUpdate={handleRowUpdate} />
      </>
    );
  }

  return (
    <Container fluid>
      {/* ── Page header ── */}
      <Box sx={{ mt: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Review class configuration quality. Assign canonical levels here; Dash-owned
          attributes are fixed in Dash.
        </Typography>
      </Box>

      {/* ── Controls toolbar ── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="ca-year-label">Tracking year</InputLabel>
            <Select labelId="ca-year-label" label="Tracking year" value={trackingYear}
              onChange={(e) => setTrackingYear(e.target.value)}>
              {trackingYearItems}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="ca-fr-label">Franchise</InputLabel>
            <Select labelId="ca-fr-label" label="Franchise" value={comCode} onChange={handleFranchiseChange}>
              <MenuItem value="">All franchises</MenuItem>
              {franchiseItems}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }} disabled={facilityOptions.length === 0}>
            <InputLabel id="ca-fac-label">Facility</InputLabel>
            <Select labelId="ca-fac-label" label="Facility" value={facilityName}
              onChange={(e) => setFacilityName(e.target.value)}>
              <MenuItem value="">All facilities</MenuItem>
              {facilityItems}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="ca-ts-label">Season</InputLabel>
            <Select labelId="ca-ts-label" label="Season" value={trackingSeason}
              onChange={(e) => setTrackingSeason(e.target.value)}>
              {trackingSeasonItems}
            </Select>
          </FormControl>

          <TextField size="small" label="Search class name" value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); setSelectedStem(null); }}
            sx={{ minWidth: 200 }} />
        </Box>

        <Box sx={{ mt: 2 }}>
          {quickViewButtons}
        </Box>

        {/* Export the current view. Disabled (with an explanation) when the result is truncated,
            so we never hand out a partial CSV that looks complete. */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" onClick={handleDownloadCsv} disabled={!canExportCsv}>
            Download CSV
          </Button>
          {truncated && (
            <Typography variant="caption" color="text.secondary">
              Narrow the filters before exporting. The current result is limited to the first {maxRows} rows.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* ── S5c: pending reusable rules ──
          Shown only when at least one ACTIVE rule has changed since the last warehouse build. It
          makes no claim about any individual class: the grid below is the warehouse's answer and is
          never optimistically mutated by a publish. This disappears on a later fetch by itself,
          because the count is re-derived and the server flips `pending_rebuild` to 0 the moment the
          suggestions model is rebuilt -- there is no state to clear. */}
      {pendingRuleCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {pendingRuleCount} published rule{pendingRuleCount === 1 ? '' : 's'}{' '}
          {pendingRuleCount === 1 ? 'is' : 'are'} awaiting the next warehouse build
          {warehouseBuiltAt ? ` (last built ${String(warehouseBuiltAt).slice(0, 10)})` : ''}. Class
          Audit shows the current warehouse mapping until then; classes those rules match will update
          after the next build.
        </Alert>
      )}

      {/* ── Prominent truncation warning ── */}
      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Showing the first {maxRows} results. Narrow the franchise, facility, status, or search
          filters to see the remaining classes.
        </Alert>
      )}

      {/* Active candidate-pattern filter (client-side, classNameStem equality) -- clearable. */}
      {selectedStem && (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`Pattern: ${selectedStem}`}
            onDelete={() => setSelectedStem(null)}
          />
        </Box>
      )}

      {body}
    </Container>
  );
}
